import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, patch } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { Avatar } from "@/components/common/Avatar";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/common/SearchInput";
import { CheckCircle2, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { dateFmt } from "@/lib/format";
import { toast } from "sonner";

export default function Approvals() {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const { data: weeks, isLoading } = useQuery({ queryKey: ["ts-approvals"], queryFn: () => get("/timesheets/approvals") });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const filtered = useMemo(() => {
    if (!weeks) return [];
    return weeks.filter((w) => {
      if (search && !(umap[w.employee_id]?.name || "").toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [weeks, umap, search]);

  const act = useMutation({
    mutationFn: ({ id, status }) => patch(`/timesheets/weeks/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ts-approvals"] }); toast.success("Timesheet reviewed"); },
  });

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="Timesheet Approvals" subtitle="Review and approve submitted timesheets." />
      {(weeks || []).length === 0 ? <EmptyState icon={CheckCircle2} title="All caught up" subtitle="No pending timesheets to review." /> : (
        <>
          <div className="mb-4 flex items-center gap-3">
            <SearchInput testid="ts-approvals-search" placeholder="Search employee name" value={search} onChange={setSearch} />
          </div>
          {filtered.length === 0 ? (
            <EmptyState icon={CheckCircle2} title="No matches" subtitle="No timesheets match your search." />
          ) : (
            <div className="space-y-2">
              {filtered.map((w) => (
                <div key={w.id} className="rounded-xl border border-border bg-card">
                  <div className="flex items-center gap-3 p-4">
                    <button onClick={() => setExpanded(expanded === w.id ? null : w.id)} className="text-muted-foreground"><ChevronDown className={cn("h-4 w-4 transition-transform", expanded === w.id && "rotate-180")} /></button>
                    <Avatar user={umap[w.employee_id]} size="sm" />
                    <div className="flex-1"><div className="text-sm font-medium">{umap[w.employee_id]?.name}</div><div className="text-xs text-muted-foreground">Week of {dateFmt(w.week_start)} · {w.total}h</div></div>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => act.mutate({ id: w.id, status: "Rejected" })}>Reject</Button>
                    <Button size="sm" onClick={() => act.mutate({ id: w.id, status: "Approved" })}>Approve</Button>
                  </div>
                  {expanded === w.id && (
                    <div className="border-t border-border p-4">
                      {(w.entries || []).map((e, i) => (
                        <div key={i} className="flex justify-between py-1 text-sm"><span>{dateFmt(e.date)} — {e.summary}</span><span className="font-mono">{e.hours}h</span></div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
